/**
 * Return the id of the exact quote a checkout is accepting.
 *
 * The quote route currently exposes both the internal quote entity and a
 * customer-facing `summary`, so readers have to tolerate either location
 * during rolling deploys. Blank strings are never valid checkout ids.
 */
export const quoteIdFrom = (quote) => {
  const direct = quote?.quoteId;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();

  const summary = quote?.summary?.quoteId;
  return typeof summary === 'string' && summary.trim()
    ? summary.trim()
    : null;
};

export const CHECKOUT_QUOTE_REQUIRED = 'CHECKOUT_QUOTE_REQUIRED';

/**
 * Ask for a second, informed click after checkout details change. Quote ids
 * remain a server-side safety mechanism; the customer-facing explanation must
 * not revive the retired requisition-capacity pricing model.
 */
export const checkoutQuoteReviewNotice = () =>
  'Your quote was refreshed. Review the current Premium plan terms and price, then start checkout again.';

export const checkoutQuoteRequiredError = () => {
  const error = new Error(
    'A current billing quote is required before checkout can start.',
  );
  error.code = CHECKOUT_QUOTE_REQUIRED;
  return error;
};

/**
 * The backend's global exception filter wraps an HttpException response under
 * `message`, while older/test deployments may return that response directly.
 * Normalize both shapes once so checkout callers cannot recognize the code but
 * accidentally miss the accompanying replacement quote.
 */
export const checkoutRefusalFrom = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

  const nested = data.message;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return { ...data, ...nested };
  }
  return data;
};

export const replacementQuoteFrom = (error) => {
  const refusal = checkoutRefusalFrom(error);
  if (
    (refusal?.code === 'QUOTE_REFRESHED' ||
      refusal?.code === 'QUOTE_EXPIRED') &&
    refusal?.quote &&
    typeof refusal.quote === 'object'
  ) {
    return { code: refusal.code, quote: refusal.quote };
  }
  return null;
};

/**
 * Resolve the quote for one checkout attempt and tell the caller whether doing
 * so replaced what the customer had on screen. Visible purchase surfaces stop
 * when `refreshed` is true so the replacement price gets a second click. A
 * handoff whose first review surface is Stripe may use ensureCheckoutQuoteId.
 */
export const resolveCheckoutQuote = async (quote, refreshQuote) => {
  const current = quoteIdFrom(quote);
  if (current) return { quoteId: current, quote, refreshed: false };

  if (typeof refreshQuote === 'function') {
    const refreshedQuote = await refreshQuote();
    const refreshedId = quoteIdFrom(refreshedQuote);
    if (refreshedId) {
      return {
        quoteId: refreshedId,
        quote: refreshedQuote,
        refreshed: true,
      };
    }
  }

  throw checkoutQuoteRequiredError();
};

/**
 * Fail closed when a checkout surface has no quote id.
 *
 * If a refresh function is supplied, give the surface one chance to replace a
 * missing/legacy quote before refusing locally. The checkout request is never
 * sent without a non-empty id.
 */
export const ensureCheckoutQuoteId = async (quote, refreshQuote) => {
  const resolved = await resolveCheckoutQuote(quote, refreshQuote);
  return resolved.quoteId;
};
